import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconCheckCircle, IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import ClientFarmSearch from '../../clients/forms/search';
import { useCreateRecipient, useUpdateRecipient } from '../contexts/recipients';

import './styles/form.css';

// Alta/edición de receptora (admin-recipients.md). Entidad liviana: identidad LOCAL a
// la finca → `number` + `farm_id` obligatorios; el `number` es único por finca. El
// dueño/cliente se deriva de la finca (no se edita acá). Reubicar = cambiar la finca.
export default function RecipientForm({ recipient, onClose, onSaved }) {
    const editing = !!recipient;
    const { mutateAsync: create, isPending: creating } = useCreateRecipient();
    const { mutateAsync: update, isPending: updating } = useUpdateRecipient(recipient?.id);
    const busy = creating || updating;
    const cardRef = useModalDialog(onClose);

    const [number, setNumber] = useState(recipient?.number || '');
    const [farm, setFarm] = useState(recipient?.farm || null); // { id, name }
    const [name, setName] = useState(recipient?.name || '');
    const [remarks, setRemarks] = useState(typeof recipient?.remarks === 'string' ? recipient.remarks : '');

    const submit = async () => {
        if (!number.trim()) { toast.error('El número es obligatorio'); return; }
        if (!farm) { toast.error('Elegí la finca (identidad por finca)'); return; }
        const payload = { number: number.trim(), farm_id: farm.id };
        if (name.trim()) payload.name = name.trim(); else if (editing) payload.name = null;
        if (remarks.trim()) payload.remarks = remarks.trim(); else if (editing) payload.remarks = null;
        try {
            if (editing) await update(payload); else await create(payload);
            toast.success(editing ? 'Receptora actualizada' : 'Receptora creada');
            onSaved?.();
        } catch (er) {
            if (er?.status === 409) toast.error('Ya existe una receptora activa con ese número en esa finca');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo guardar la receptora');
        }
    };

    return (
        <div className="rcp-modal" role="dialog" aria-modal="true" aria-label={editing ? 'Editar receptora' : 'Nueva receptora'}>
            <div className="rcp-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="rcp-modal__card" ref={cardRef}>
                <header className="rcp-modal__head">
                    <div>
                        <h2 className="rcp-modal__title">{editing ? 'Editar receptora' : 'Nueva receptora'}</h2>
                        <span className="rcp-modal__sub">El número es único por finca; el cliente se deriva de la finca.</span>
                    </div>
                    <button type="button" className="rcp-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="rcp-form">
                    <div className="rcp-form__row">
                        <label className="rcp-form__field">
                            <span className="rcp-form__label">Número</span>
                            <input type="text" maxLength={40} value={number} onChange={(e) => setNumber(e.target.value)} disabled={busy} placeholder="Ej. 01" />
                        </label>
                        <label className="rcp-form__field">
                            <span className="rcp-form__label">Nombre</span>
                            <input type="text" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} disabled={busy} placeholder="Opcional" />
                        </label>
                    </div>

                    <div className="rcp-form__field">
                        <span className="rcp-form__label">Finca (identidad)</span>
                        {farm ? (
                            <span className="rcp-form__picked">
                                <IconCheckCircle /> {farm.name || `Finca #${farm.id}`}
                                <button type="button" className="rcp-form__clear" onClick={() => setFarm(null)} disabled={busy} aria-label="Cambiar finca">×</button>
                            </span>
                        ) : (
                            <ClientFarmSearch scope="all" farmsOnly placeholder="Buscar finca…" onSelect={(it) => setFarm({ id: it.id, name: it.name })} />
                        )}
                    </div>

                    <label className="rcp-form__field">
                        <span className="rcp-form__label">Notas</span>
                        <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={busy} placeholder="Opcional" />
                    </label>
                </div>

                <footer className="rcp-modal__foot">
                    <button type="button" className="rcp-btn rcp-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="rcp-btn rcp-btn--primary" onClick={submit} disabled={busy || !number.trim() || !farm}>
                        {busy ? 'Guardando…' : editing ? 'Guardar' : 'Crear'}
                    </button>
                </footer>
            </div>
        </div>
    );
}
